import React from 'react';
import { Star } from 'lucide-react';
import { FadeUp, EYEBROW, GRAD_TEXT } from './shared';

const STATS = [
  { value: '2,000+', label: 'Successful migrations' },
  { value: '20+', label: 'Countries assessed' },
  { value: '5 min', label: 'Average assessment' },
  { value: '4.9/5', label: 'Client rating' },
];

const TESTIMONIALS = [
  { name: 'Rahul Sharma', quote: 'I had spoken to three different consultants and got three completely different answers. The Axpats AI Assessment finally explained why Australia was my strongest option instead of just trying to sell me a package. The report was detailed, easy to understand, and helped me prepare meaningful questions before booking my consultation.' },
  { name: 'Priya Nair', quote: "I honestly thought I wasn't eligible because I'm over 35. The assessment didn't just say yes or no—it explained what factors were helping my profile and what could improve it. I finally felt like someone was looking at my actual situation instead of giving a generic answer." },
  { name: 'Ahmed Khan', quote: "I've been receiving migration calls for years and never knew who to trust. Before spending any money, I completed the assessment and got a professional report that actually made sense. It gave me confidence that I was asking the right questions during my consultation." },
  { name: 'Ayesha Malik', quote: 'My biggest confusion was whether I should apply for Australia, Canada, or Europe. The assessment compared my profile instead of pushing one country. It saved me weeks of research and helped me focus on the options that genuinely suited my experience.' },
  { name: 'Maria Santos', quote: 'As a healthcare professional, I was overwhelmed by all the different visa information online. The assessment broke everything down into simple language and showed exactly where my profile was strongest. I wish I had found this much earlier.' },
  { name: 'John Dela Cruz', quote: "I liked that the assessment didn't promise guaranteed approval. Instead, it clearly explained my strengths, possible risks, and the documents I would eventually need. It felt transparent and honest." },
  { name: 'Fatima', quote: "I wasn't even sure whether employer sponsorship or skilled migration was better for me. The assessment explained the differences and gave me a clear direction instead of leaving me even more confused. The report looked very professional." },
  { name: 'Omar', quote: "I've spent months watching YouTube videos and reading Facebook groups. In just a few minutes, the assessment organized everything into a personalised migration plan. It saved me a huge amount of time." },
  { name: 'Mohammed Hassan', quote: "I was worried about hidden costs because I'd heard too many stories about consultants adding unexpected fees later. The assessment explained the overall migration process first, which made me feel much more informed before speaking with anyone." },
  { name: 'Sarah Fernandez', quote: "What impressed me most was how personalised the report felt. It wasn't just a score—it explained my work experience, education, age, and family situation together, then recommended the countries where I had the strongest chances. The consultation afterwards felt much more productive because I already understood my profile." },
];

const AVATAR_TONES = [
  { bg: 'rgba(246,116,37,0.12)', color: '#F67425' },
  { bg: 'rgba(11,129,90,0.1)', color: '#0B815A' },
  { bg: 'rgba(187,5,24,0.08)', color: '#BB0518' },
];

const initials = (name) => name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

const Testimonials = ({ onStart }) => (
  <section data-testid="testimonials-section" style={{ padding: '0 1.5rem 7rem', maxWidth: '1024px', margin: '0 auto' }}>
    {/* Stats band */}
    <FadeUp>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        border: '1px solid rgba(48,43,44,0.1)', borderRadius: '2px', background: '#fff',
        marginBottom: '4rem', overflow: 'hidden',
      }}>
        {STATS.map((s, i) => (
          <div key={s.label} data-testid={`stat-${i}`} style={{
            padding: '1.75rem 1rem', textAlign: 'center',
            borderRight: i < STATS.length - 1 ? '1px solid rgba(48,43,44,0.08)' : 'none',
          }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 900, ...GRAD_TEXT }}>{s.value}</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', fontWeight: 600, color: 'rgba(48,43,44,0.6)', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>
    </FadeUp>

    <FadeUp>
      <div style={{ marginBottom: '2.5rem' }}>
        <span style={EYEBROW}>Success Stories</span>
        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', fontWeight: 800, color: '#302B2C', letterSpacing: '-0.03em', lineHeight: 1.05, marginTop: '0.75rem' }}>
          They started with <span style={GRAD_TEXT}>one upload</span>
        </h2>
      </div>
    </FadeUp>

    {/* Auto-scrolling carousel: two rows drifting in opposite directions, pause on hover */}
    <div className="tm-carousel" data-testid="testimonial-carousel">
      {[0, 1].map(row => {
        const items = TESTIMONIALS.filter((_, i) => i % 2 === row);
        return (
          <div key={row} className="tm-track-wrap">
            <div className={row === 0 ? 'tm-track' : 'tm-track tm-track-reverse'}>
              {[...items, ...items].map((t, idx) => {
                const origIdx = TESTIMONIALS.indexOf(t);
                const tone = AVATAR_TONES[origIdx % AVATAR_TONES.length];
                const isClone = idx >= items.length;
                return (
                  <div
                    key={`${t.name}-${idx}`}
                    data-testid={isClone ? undefined : `testimonial-${origIdx}`}
                    aria-hidden={isClone || undefined}
                    className="tm-card"
                    style={{
                      padding: '1.75rem', background: '#fff',
                      border: '1px solid rgba(48,43,44,0.1)', borderRadius: '2px',
                      display: 'flex', flexDirection: 'column', gap: '1rem',
                    }}
                  >
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[0, 1, 2, 3, 4].map(s => <Star key={s} size={13} fill="#F67425" color="#F67425" />)}
                    </div>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.84rem', color: 'rgba(48,43,44,0.78)', lineHeight: 1.65, margin: 0, flex: 1 }}>
                      “{t.quote}”
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: tone.bg, color: tone.color,
                        fontFamily: 'var(--font-heading)', fontSize: '0.8rem', fontWeight: 800,
                      }}>{initials(t.name)}</span>
                      <div>
                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '0.85rem', fontWeight: 700, color: '#302B2C' }}>{t.name}</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: '#0B815A', letterSpacing: '0.03em' }}>Verified Assessment</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

export default Testimonials;
